import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Shield, FileText } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { SITE, breadcrumbForPage } from "@/lib/seo";
import { cn } from "@/lib/utils";

type Section = {
  id: string;
  num: string;
  title: string;
  html: string;
};

const sections: Section[] = [
  {
    id: "responsable",
    num: "1",
    title: "Responsable del tratamiento",
    html: `<ul class="list-disc pl-5 space-y-1">
<li><strong>Titular:</strong> Vibra Marketing S.L.</li>
<li><strong>CIF:</strong> B25913948</li>
<li><strong>Domicilio:</strong> Calle Remolino 49B, 1ºE, 28710 El Molar (Madrid), España</li>
<li><strong>Email de contacto:</strong> <!--email_off--><a href="mailto:direccion@vibramarketing.es" class="text-primary hover:underline">direccion@vibramarketing.es</a><!--/email_off--></li>
<li><strong>Web:</strong> <a href="https://vibramarketing.es" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">https://vibramarketing.es</a></li>
</ul>
<p class="mt-3">Vibra Marketing S.L. (en adelante, "Vibra Marketing") cumple el Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018 (LOPDGDD).</p>`,
  },
  {
    id: "datos",
    num: "2",
    title: "Qué datos recogemos y cómo",
    html: `<p>Esta web no contiene formularios de contacto. Los datos personales que tratamos llegan por estas vías:</p>
<ul class="list-disc pl-5 space-y-2 mt-3">
<li><strong>Agenda de reuniones (Cal.com):</strong> nombre, email y, en su caso, teléfono y datos que facilites al reservar una llamada o videollamada con nosotros.</li>
<li><strong>WhatsApp:</strong> tu número de teléfono, nombre de perfil y el contenido de los mensajes cuando nos escribes a través del botón de WhatsApp de la web.</li>
<li><strong>Llamadas telefónicas:</strong> las llamadas comerciales pueden ser grabadas a través de nuestro sistema de telefonía (Zadarma) con fines de calidad y de acreditación de lo acordado. Se informa de la grabación al inicio de la llamada.</li>
<li><strong>Anuncios con formulario (Meta Lead Ads):</strong> si nos facilitas tus datos a través de un formulario de nuestros anuncios en Facebook o Instagram, recibimos los datos que incluyas (normalmente nombre, email y teléfono).</li>
<li><strong>Contratación y facturación:</strong> datos identificativos, fiscales y de los servicios contratados cuando te conviertes en cliente. El pago se procesa en una página segura de Stripe; <strong>no almacenamos los datos de tu tarjeta</strong>.</li>
<li><strong>Onboarding de clientes:</strong> información sobre tu negocio, accesos y recursos que nos facilitas para prestarte el servicio.</li>
<li><strong>Navegación:</strong> datos técnicos (IP, dispositivo, páginas vistas) mediante cookies, solo si las aceptas (ver <a href="/cookies" class="text-primary hover:underline">Política de Cookies</a>).</li>
</ul>
<p class="mt-3">Los datos de contactos, leads y clientes se almacenan principalmente en nuestro CRM (Kommo).</p>
<p class="mt-3">No recogemos datos de categorías especiales ni de menores de 14 años. Si detectamos datos de un menor, los eliminaremos.</p>`,
  },
  {
    id: "base-legal",
    num: "3",
    title: "Para qué usamos tus datos y con qué base legal",
    html: `<div class="overflow-x-auto mt-2">
<table class="w-full text-sm border border-border/60 rounded-lg overflow-hidden">
<thead class="bg-muted/50 text-foreground"><tr><th class="text-left p-2 font-semibold">Finalidad</th><th class="text-left p-2 font-semibold">Base legal</th></tr></thead>
<tbody>
<tr class="border-t border-border/60"><td class="p-2">Gestionar las reuniones que agendas y atender tus consultas (Cal.com, WhatsApp, teléfono)</td><td class="p-2">Medidas precontractuales a tu solicitud (art. 6.1.b RGPD)</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Grabación de llamadas comerciales para calidad y prueba de lo acordado</td><td class="p-2">Interés legítimo (art. 6.1.f)</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Gestionar los leads recibidos de formularios de Meta y contactarte en relación con tu solicitud</td><td class="p-2">Consentimiento prestado en el formulario (art. 6.1.a)</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Enviar comunicaciones comerciales por email a los leads captados mediante dichos formularios</td><td class="p-2">Consentimiento (art. 6.1.a); baja disponible en cada envío</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Prestar los servicios contratados y gestionar la relación con clientes</td><td class="p-2">Ejecución de contrato (art. 6.1.b)</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Facturación, contabilidad y obligaciones fiscales</td><td class="p-2">Obligación legal (art. 6.1.c)</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Medición del uso de la web y de nuestras campañas (cookies analíticas y de marketing)</td><td class="p-2">Consentimiento (art. 6.1.a)</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Defensa frente a reclamaciones</td><td class="p-2">Interés legítimo (art. 6.1.f)</td></tr>
</tbody>
</table>
</div>
<p class="mt-3">Puedes oponerte a recibir comunicaciones comerciales en cualquier momento mediante el enlace de baja de cada email o escribiendo a <!--email_off--><a href="mailto:direccion@vibramarketing.es" class="text-primary hover:underline">direccion@vibramarketing.es</a><!--/email_off-->.</p>`,
  },
  {
    id: "conservacion",
    num: "4",
    title: "Cuánto tiempo conservamos los datos",
    html: `<ul class="list-disc pl-5 space-y-2">
<li><strong>Leads y consultas:</strong> hasta 1 año desde el último contacto, salvo que solicites antes su supresión.</li>
<li><strong>Grabaciones de llamadas:</strong> el tiempo necesario para la finalidad de calidad y prueba, con un máximo de 1 año, salvo que deban conservarse para la defensa frente a reclamaciones.</li>
<li><strong>Clientes:</strong> durante la relación contractual y, después, bloqueados durante los plazos de prescripción legal (con carácter general, 5 años para obligaciones contractuales y 4 años para obligaciones fiscales).</li>
<li><strong>Comunicaciones comerciales:</strong> hasta que retires el consentimiento o te des de baja.</li>
<li><strong>Cookies:</strong> según los plazos de la <a href="/cookies" class="text-primary hover:underline">Política de Cookies</a>.</li>
</ul>`,
  },
  {
    id: "comunicacion",
    num: "5",
    title: "A quién comunicamos tus datos",
    html: `<p>No vendemos tus datos. Los compartimos únicamente con los proveedores que necesitamos para operar (encargados del tratamiento o responsables independientes, según el caso):</p>
<ul class="list-disc pl-5 space-y-1 mt-3">
<li><strong>Kommo</strong> — CRM donde almacenamos contactos, leads y clientes</li>
<li><strong>Cal.com</strong> — agenda de reuniones</li>
<li><strong>Zadarma</strong> — telefonía y grabación de llamadas</li>
<li><strong>Stripe</strong> — procesamiento de pagos (en su propia página segura)</li>
<li><strong>Klarna</strong> — financiación en 3 plazos (actúa como responsable independiente de su evaluación crediticia)</li>
<li><strong>Meta</strong> — formularios de leads y medición publicitaria</li>
<li><strong>Google</strong> — analítica (GA4) y medición publicitaria</li>
<li><strong>TikTok</strong> — medición publicitaria</li>
<li><strong>Brevo</strong> — envío de emails a leads y clientes</li>
<li><strong>Lovable</strong> — alojamiento de la web</li>
<li><strong>WhatsApp (Meta)</strong> — mensajería</li>
</ul>
<p class="mt-3">Y con las <strong>Administraciones públicas</strong> cuando exista obligación legal.</p>
<p class="mt-3">Algunos de estos proveedores están ubicados fuera del Espacio Económico Europeo (principalmente en Estados Unidos). Las transferencias se amparan en decisiones de adecuación, como el Marco de Privacidad de Datos UE-EE. UU., o en cláusulas contractuales tipo aprobadas por la Comisión Europea.</p>`,
  },
  {
    id: "terceros",
    num: "6",
    title: "Datos de terceros que nos facilitan nuestros clientes",
    html: `<p>Cuando un cliente nos da acceso a sus bases de datos (por ejemplo, contactos para servicios de email marketing o CRM), <strong>el cliente es el responsable del tratamiento y Vibra Marketing actúa como encargado</strong> conforme al art. 28 RGPD: tratamos esos datos únicamente siguiendo sus instrucciones, para las finalidades del servicio contratado, y los eliminamos o devolvemos al finalizar. El cliente garantiza que dispone de base legal para el tratamiento de dichos datos.</p>`,
  },
  {
    id: "derechos",
    num: "7",
    title: "Tus derechos",
    html: `<p>Puedes ejercer en cualquier momento tus derechos de <strong>acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad</strong>, así como retirar tu consentimiento, escribiendo a <!--email_off--><a href="mailto:direccion@vibramarketing.es" class="text-primary hover:underline">direccion@vibramarketing.es</a><!--/email_off--> indicando el derecho que ejerces. Podremos solicitarte documentación para verificar tu identidad.</p>
<p class="mt-3">Responderemos en el plazo máximo de un mes. Si consideras que no hemos atendido correctamente tus derechos, puedes reclamar ante la <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">Agencia Española de Protección de Datos</a>.</p>`,
  },
  {
    id: "seguridad",
    num: "8",
    title: "Seguridad",
    html: `<p>Aplicamos medidas técnicas y organizativas apropiadas: acceso restringido a los datos, contraseñas seguras, cifrado en tránsito (SSL/HTTPS) y proveedores con garantías. Ningún sistema es infalible, pero trabajamos para minimizar los riesgos.</p>`,
  },
  {
    id: "cambios",
    num: "9",
    title: "Cambios en esta política",
    html: `<p>Podemos actualizar esta política para adaptarla a cambios legales o de nuestros servicios. La versión vigente estará siempre publicada en esta página con su fecha de actualización.</p>`,
  },
];

export default function PrivacidadPage() {
  const [active, setActive] = useState<string>(sections[0].id);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="es" />
        <title>Política de Privacidad | Vibra Marketing</title>
        <meta name="description" content="Política de privacidad de Vibra Marketing: cómo recogemos, usamos y protegemos tus datos personales conforme al RGPD." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE.url}/privacidad`} />
        <link rel="alternate" hrefLang="es-ES" href={`${SITE.url}/privacidad`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content="Política de Privacidad | Vibra Marketing" />
        <meta property="og:description" content="Cómo recogemos, usamos y protegemos tus datos personales conforme al RGPD." />
        <meta property="og:url" content={`${SITE.url}/privacidad`} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbForPage([
          { name: "Inicio", path: "/" },
          { name: "Política de privacidad", path: "/privacidad" },
        ]))}</script>
      </Helmet>
      <Header />

      <main className="relative overflow-hidden pt-28 pb-24">
        {/* Purple aura background */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-[520px] -z-10 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent" />
          <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-primary/25 blur-[120px]" />
        </div>

        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-5">
              <Shield size={14} />
              Legal
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] mb-5">
              Política de <span className="text-gradient">privacidad</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Titular: Vibra Marketing S.L. · CIF B25913948 · Calle Remolino 49B, 1ºE, 28710 El Molar (Madrid) · <span dangerouslySetInnerHTML={{ __html: '<!--email_off-->direccion@vibramarketing.es<!--/email_off-->' }} />
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Última actualización: <time dateTime="2026-06-12">12/06/2026</time>
            </p>
          </div>

          <div className="grid w-full min-w-0 lg:grid-cols-[260px_minmax(0,1fr)] gap-10 max-w-6xl mx-auto">
            {/* Sidebar nav */}
            <aside className="min-w-0 max-w-full lg:sticky lg:top-24 lg:self-start overflow-hidden">
              <nav className="flex min-w-0 max-w-full lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {sections.map((s) => {
                  const isActive = active === s.id;
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActive(s.id);
                        document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className={cn(
                        "shrink-0 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left whitespace-nowrap lg:whitespace-normal",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                          : "text-muted-foreground hover:text-foreground hover:bg-card"
                      )}
                    >
                      <span className="text-[11px] font-bold opacity-70">{s.num}</span>
                      <span>{s.title}</span>
                    </a>
                  );
                })}
              </nav>
            </aside>

            {/* Content */}
            <div className="min-w-0 space-y-8">
              {sections.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  data-reveal
                  className="scroll-mt-28 min-w-0 max-w-full rounded-2xl bg-card border border-border/60 p-5 sm:p-6 md:p-8 transition-all overflow-hidden"
                  onMouseEnter={() => setActive(s.id)}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-semibold text-primary/80 uppercase tracking-wider">Apartado {s.num}</span>
                      <h2 className="text-xl font-bold tracking-tight">{s.title}</h2>
                    </div>
                  </div>
                  <div
                    className="min-w-0 text-muted-foreground leading-relaxed space-y-2 break-words [&_a]:break-words"
                    dangerouslySetInnerHTML={{ __html: s.html }}
                  />
                </section>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
